import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Grid,
  LinearProgress,
  Chip,
  Stack,
  Collapse,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as SuccessIcon,
  CheckCircle,
  Error as ErrorIcon,
  Info as InfoIcon,
  TabletAndroid as TabletIcon,
  CloudSync as CloudSyncIcon,
  LocationOn as LocationIcon,
  DevicesOther as DevicesIcon,
  Router as RouterIcon,
  Wifi as WifiIcon,
  IntegrationInstructions as IntegrationIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Launch as LaunchIcon,
  Home as HomeIcon,
  Folder as FolderIcon,
  Message as MessageIcon,
  LocalHospital as MedicalIcon,
  Lightbulb as LightbulbIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hexnodeApi, unifiApi, clearvoxApi, emergencyServicesApi } from '../services/apiClient';
import { twonApi } from '../services/twonService';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useThemeSettings } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';
import TwoNConfigDialog from '../components/Integrations/TwoN/TwoNConfigDialog';
import DeviceImportDialog from '../components/Integrations/TwoN/DeviceImportDialog';

const IntegrationManagement = () => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  // Hexnode state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [hexnodeExpanded, setHexnodeExpanded] = useState(false);

  // UniFi state
  const [unifiImporting, setUnifiImporting] = useState(false);
  const [unifiResult, setUnifiResult] = useState(null);
  const [unifiConfirmOpen, setUnifiConfirmOpen] = useState(false);
  const [unifiExpanded, setUnifiExpanded] = useState(false);

  // Clearvox state
  const [clearvoxImporting, setClearvoxImporting] = useState(false);
  const [clearvoxResult, setClearvoxResult] = useState(null);
  const [clearvoxConfirmOpen, setClearvoxConfirmOpen] = useState(false);
  const [clearvoxExpanded, setClearvoxExpanded] = useState(false);
  const [clearvoxStats, setClearvoxStats] = useState(null);
  const [clearvoxSites, setClearvoxSites] = useState([]);
  const [clearvoxSitesLoading, setClearvoxSitesLoading] = useState(false);
  const [clearvoxSelectedSites, setClearvoxSelectedSites] = useState([]);
  const [clearvoxSyncProgress, setClearvoxSyncProgress] = useState({});

  // 2N state
  const [twonImporting, setTwonImporting] = useState(false);
  const [twonResult, setTwonResult] = useState(null);
  const [twonExpanded, setTwonExpanded] = useState(false);
  const [twonStats, setTwonStats] = useState(null);
  const [twonConfigOpen, setTwonConfigOpen] = useState(false);
  const [twonImportOpen, setTwonImportOpen] = useState(false);
  const [twonConfig, setTwonConfig] = useState(null);
  const [twonCommanders, setTwonCommanders] = useState([]);

  // Emergency Services state
  const [emergencyExpanded, setEmergencyExpanded] = useState(false);
  const [emergencyStats, setEmergencyStats] = useState(null);
  const [emergencyProviders, setEmergencyProviders] = useState([]);

  // Check permissions - user must have devices.create OR devices.edit
  const canManageDevices = hasPermission('devices.edit') || hasPermission('devices.create');

  useEffect(() => {
    // Only fetch status if user has permissions
    if (!canManageDevices) return;

    fetchImportStatus();
    fetchClearvoxStats();
    fetchTwonStats();
    fetchEmergencyStats();
    // Refresh status every 10 seconds if syncing
    const interval = setInterval(() => {
      if (syncing) {
        fetchImportStatus();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [syncing, canManageDevices]);

  const fetchImportStatus = async () => {
    try {
      setLoadingStatus(true);
      const response = await hexnodeApi.getImportStatus();
      if (response.data.success) {
        setImportStatus(response.data.status);

        // Check if import is actually running
        const importProgress = response.data.status.import;
        if (importProgress && importProgress.isRunning) {
          // Import is running, keep syncing state true
          if (!syncing) {
            setSyncing(true);
          }
        } else if (importProgress && !importProgress.isRunning && syncing) {
          // Import was running but has now completed
          setSyncing(false);
          if (importProgress.completedAt) {
            enqueueSnackbar('Import completed successfully!', { variant: 'success' });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching import status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      setConfirmDialogOpen(false);

      const response = await hexnodeApi.importAll();

      if (response.data.success) {
        setSyncResult({
          success: true,
          message: response.data.message || t('MDMSettings.default_success_message'),
          startedAt: response.data.startedAt
        });
        enqueueSnackbar(t('MDMSettings.snackbar_success'), { variant: 'success' });

        // Status polling will handle completion detection automatically
      } else {
        throw new Error(response.data.error || t('MDMSettings.default_error_message'));
      }
    } catch (error) {
      setSyncResult({
        success: false,
        message: error.response?.data?.error || error.message || t('MDMSettings.snackbar_error')
      });
      enqueueSnackbar(error.response?.data?.error || t('MDMSettings.snackbar_error'), { variant: 'error' });
      setSyncing(false);
    }
  };

  const handleUniFiImport = async () => {
    try {
      setUnifiImporting(true);
      setUnifiResult(null);
      setUnifiConfirmOpen(false);

      const response = await unifiApi.importDevices({ createNew: true });

      if (response.data.success) {
        setUnifiResult({
          success: true,
          message: response.data.message || 'UniFi devices imported successfully',
          stats: response.data.stats || {}
        });
        enqueueSnackbar('UniFi devices imported successfully', { variant: 'success' });
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (error) {
      setUnifiResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to import UniFi devices'
      });
      enqueueSnackbar(error.response?.data?.error || 'Failed to import UniFi devices', { variant: 'error' });
    } finally {
      setUnifiImporting(false);
    }
  };

  const fetchClearvoxStats = async () => {
    try {
      const response = await clearvoxApi.getStats();
      if (response.data.success) {
        setClearvoxStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching Clearvox stats:', error);
    }
  };

  const fetchClearvoxSites = async () => {
    try {
      setClearvoxSitesLoading(true);
      const response = await clearvoxApi.discoverSites();
      if (response.data.success && response.data.data?.sites) {
        setClearvoxSites(response.data.data.sites);
        // Auto-select all connected sites by default
        const connectedSiteIds = response.data.data.sites
          .filter(s => s.connectionStatus === 'connected')
          .map(s => s.siteLocationId);
        setClearvoxSelectedSites(connectedSiteIds);
      }
    } catch (error) {
      console.error('Error fetching Clearvox sites:', error);
      enqueueSnackbar('Failed to fetch Clearvox sites', { variant: 'error' });
    } finally {
      setClearvoxSitesLoading(false);
    }
  };

  const fetchTwonStats = async () => {
    try {
      const [statsResponse, commandersResponse] = await Promise.all([
        twonApi.getStats(),
        twonApi.getAllConfigs(),
      ]);

      if (statsResponse.data.success) {
        setTwonStats(statsResponse.data.stats);
      }

      if (commandersResponse.data.success || commandersResponse.data.data) {
        const commanders = commandersResponse.data.data || [];
        setTwonCommanders(commanders);
        // Keep twonConfig for backward compatibility (use first active Commander)
        setTwonConfig(commanders.find(c => c.is_active !== false) || null);
      }
    } catch (error) {
      console.error('Error fetching 2N stats:', error);
      setTwonCommanders([]);
      setTwonConfig(null);
    }
  };

  const fetchEmergencyStats = async () => {
    try {
      const [statsResponse, providersResponse] = await Promise.all([
        emergencyServicesApi.getStats(),
        emergencyServicesApi.getProviders(),
      ]);

      if (statsResponse.data.success) {
        setEmergencyStats(statsResponse.data.data);
      }

      if (providersResponse.data.success) {
        setEmergencyProviders(providersResponse.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching emergency services stats:', error);
      setEmergencyStats(null);
      setEmergencyProviders([]);
    }
  };

  const handleClearvoxSiteToggle = (siteId) => {
    setClearvoxSelectedSites(prev => {
      if (prev.includes(siteId)) {
        return prev.filter(id => id !== siteId);
      } else {
        return [...prev, siteId];
      }
    });
  };

  const handleClearvoxSelectAll = () => {
    const connectedSiteIds = clearvoxSites
      .filter(s => s.connectionStatus === 'connected')
      .map(s => s.siteLocationId);
    setClearvoxSelectedSites(connectedSiteIds);
  };

  const handleClearvoxDeselectAll = () => {
    setClearvoxSelectedSites([]);
  };

  const handleClearvoxImport = async () => {
    try {
      setClearvoxImporting(true);
      setClearvoxResult(null);
      setClearvoxConfirmOpen(false);
      setClearvoxSyncProgress({});

      const sitesToSync = clearvoxSelectedSites.length > 0
        ? clearvoxSelectedSites
        : clearvoxSites.filter(s => s.connectionStatus === 'connected').map(s => s.siteLocationId);

      if (sitesToSync.length === 0) {
        enqueueSnackbar('No sites selected for sync', { variant: 'warning' });
        setClearvoxImporting(false);
        return;
      }

      const results = {
        total: sitesToSync.length,
        successful: 0,
        failed: 0,
        siteResults: []
      };

      // Sync each site sequentially to show progress
      for (const siteId of sitesToSync) {
        const site = clearvoxSites.find(s => s.siteLocationId === siteId);
        const siteName = site?.siteName || `Site ${siteId}`;

        // Update progress state
        setClearvoxSyncProgress(prev => ({
          ...prev,
          [siteId]: { status: 'syncing', message: `Syncing ${siteName}...` }
        }));

        try {
          const response = await clearvoxApi.importSite(siteId);

          if (response.data.success) {
            results.successful++;
            results.siteResults.push({
              siteId,
              siteName,
              success: true,
              stats: response.data.stats || response.data.results
            });

            setClearvoxSyncProgress(prev => ({
              ...prev,
              [siteId]: {
                status: 'success',
                message: `${siteName} synced successfully`,
                stats: response.data.stats || response.data.results
              }
            }));
          } else {
            throw new Error(response.data.error || 'Import failed');
          }
        } catch (error) {
          results.failed++;
          results.siteResults.push({
            siteId,
            siteName,
            success: false,
            error: error.response?.data?.error || error.message
          });

          setClearvoxSyncProgress(prev => ({
            ...prev,
            [siteId]: {
              status: 'error',
              message: `${siteName} failed: ${error.response?.data?.error || error.message}`
            }
          }));
        }
      }

      // Set final result
      if (results.failed === 0) {
        setClearvoxResult({
          success: true,
          message: `All ${results.successful} sites synced successfully`,
          stats: results
        });
        enqueueSnackbar(`Clearvox sync completed: ${results.successful} sites synced`, { variant: 'success' });
      } else if (results.successful > 0) {
        setClearvoxResult({
          success: true,
          message: `Partial sync: ${results.successful} succeeded, ${results.failed} failed`,
          stats: results
        });
        enqueueSnackbar(`Partial sync: ${results.successful} succeeded, ${results.failed} failed`, { variant: 'warning' });
      } else {
        setClearvoxResult({
          success: false,
          message: `All ${results.failed} sites failed to sync`,
          stats: results
        });
        enqueueSnackbar('All sites failed to sync', { variant: 'error' });
      }

      fetchClearvoxStats(); // Refresh stats
    } catch (error) {
      setClearvoxResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to import Clearvox users'
      });
      enqueueSnackbar(error.response?.data?.error || 'Failed to import Clearvox users', { variant: 'error' });
    } finally {
      setClearvoxImporting(false);
    }
  };

  // Sync a single site
  const handleSyncSingleSite = async (siteId) => {
    const site = clearvoxSites.find(s => s.siteLocationId === siteId);
    const siteName = site?.siteName || `Site ${siteId}`;

    setClearvoxSyncProgress(prev => ({
      ...prev,
      [siteId]: { status: 'syncing', message: `Syncing ${siteName}...` }
    }));

    try {
      const response = await clearvoxApi.importSite(siteId);

      if (response.data.success) {
        setClearvoxSyncProgress(prev => ({
          ...prev,
          [siteId]: {
            status: 'success',
            message: `${siteName} synced successfully`,
            stats: response.data.stats || response.data.results
          }
        }));
        enqueueSnackbar(`${siteName} synced successfully`, { variant: 'success' });
        fetchClearvoxStats();
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (error) {
      setClearvoxSyncProgress(prev => ({
        ...prev,
        [siteId]: {
          status: 'error',
          message: `${siteName} failed: ${error.response?.data?.error || error.message}`
        }
      }));
      enqueueSnackbar(`Failed to sync ${siteName}`, { variant: 'error' });
    }
  };

  const handleTwonSync = async () => {
    try {
      setTwonImporting(true);
      setTwonResult(null);

      const response = await twonApi.syncDevices();

      if (response.data.success) {
        setTwonResult({
          success: true,
          message: '2N device sync completed successfully',
          stats: response.data.stats
        });
        enqueueSnackbar('2N devices synced successfully', { variant: 'success' });
        fetchTwonStats(); // Refresh stats
      } else {
        throw new Error(response.data.error || 'Sync failed');
      }
    } catch (error) {
      setTwonResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to sync 2N devices'
      });
      enqueueSnackbar(error.response?.data?.error || 'Failed to sync 2N devices', { variant: 'error' });
    } finally {
      setTwonImporting(false);
    }
  };

  // Integration card component for reusability
  const IntegrationCard = ({
    title,
    description,
    icon: Icon,
    color,
    status,
    stats,
    onSync,
    syncing,
    syncResult,
    expanded,
    onToggleExpanded,
    children,
    comingSoon = false,
    inDevelopment = false
  }) => (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          elevation: 6,
          transform: comingSoon ? 'none' : 'translateY(-4px)',
        },
        opacity: comingSoon ? 0.6 : 1,
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {comingSoon && (
        <Chip
          label="Coming Later"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            fontWeight: 600
          }}
          color="default"
        />
      )}
      {inDevelopment && (
        <Chip
          label="In Development"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            fontWeight: 600
          }}
          color="warning"
        />
      )}

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>

        {/* Status Chip */}
        {!comingSoon && status && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={status}
              size="small"
              color={syncing ? 'warning' : inDevelopment ? 'warning' : 'success'}
              icon={syncing ? <CircularProgress size={16} /> : inDevelopment ? <InfoIcon /> : <CheckCircle />}
            />
          </Box>
        )}

        {/* Stats Grid */}
        {!comingSoon && stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {stats.map((stat, index) => (
              <Grid item xs={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Sync Result */}
        {!comingSoon && syncResult && (
          <Alert
            severity={syncResult.success ? 'success' : 'error'}
            sx={{ mb: 2 }}
            onClose={() => {
              if (title === 'Hexnode MDM') setSyncResult(null);
              if (title === 'UniFi Network') setUnifiResult(null);
              if (title === 'Clearvox VoIP') setClearvoxResult(null);
            }}
          >
            <AlertTitle>{syncResult.success ? 'Success' : 'Error'}</AlertTitle>
            <Typography variant="body2">{syncResult.message}</Typography>
            {syncResult.stats && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Created: {syncResult.stats.created || 0} | Updated: {syncResult.stats.updated || 0} | Failed: {syncResult.stats.failed || 0}
              </Typography>
            )}
          </Alert>
        )}
      </CardContent>

      {/* Actions */}
      {!comingSoon && (
        <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          <Button
            variant="contained"
            color={color}
            size="large"
            fullWidth
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={onSync}
            // disable when syncing or in development
            disabled={syncing || inDevelopment}
            sx={{ mb: 1 }}
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>

          <Button
            size="small"
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={onToggleExpanded}
            sx={{ alignSelf: 'center' }}
          >
            {expanded ? 'Show Less' : 'Show More'}
          </Button>

          <Collapse in={expanded}>
            <Box sx={{ mt: 2 }}>
              {children}
            </Box>
          </Collapse>
        </CardActions>
      )}
    </Card>
  );

  // If user doesn't have permission, show access denied page
  if (!canManageDevices) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You do not have permission to access Integration Management. This page requires device creation or editing permissions.
          </Typography>
          <Alert severity="info" sx={{ textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
            <AlertTitle>Required Permissions</AlertTitle>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="devices.create - Create new devices" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="devices.edit - Edit existing devices" />
              </ListItem>
            </List>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Contact your administrator to request these permissions.
            </Typography>
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IntegrationIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Integration Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage and sync third-party integrations with CARO
        </Typography>
      </Box>

      {/* Integration Cards Grid */}
      <Grid container spacing={3}>
        {/* Hexnode Integration Card */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Hexnode MDM"
            description="Mobile device management for Android tablets"
            icon={TabletIcon}
            color="primary"
            status={syncing ? 'Syncing...' : 'Connected'}
            stats={importStatus ? [
              { label: 'Total', value: importStatus.totalHexnodeDevices || 0, color: 'primary.main' },
              { label: 'Mapped', value: importStatus.mappedToLocations || 0, color: 'success.main' },
              { label: 'Unmapped', value: importStatus.unmappedDevices || 0, color: 'warning.main' }
            ] : null}
            onSync={() => setConfirmDialogOpen(true)}
            syncing={syncing}
            syncResult={syncResult}
            expanded={hexnodeExpanded}
            onToggleExpanded={() => setHexnodeExpanded(!hexnodeExpanded)}
          >
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              What gets synced:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="All Android tablets from Hexnode"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Device status and location data"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Auto-link to CARO locations"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>

            {syncing && importStatus?.import && importStatus.import.progress && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Progress: {importStatus.import.progress.processed} / {importStatus.import.progress.total}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={importStatus.import.progress.total > 0
                    ? (importStatus.import.progress.processed / importStatus.import.progress.total) * 100
                    : 0}
                />
              </Box>
            )}
          </IntegrationCard>
        </Grid>

        {/* UniFi Integration Card */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="UniFi Network"
            description="Network infrastructure devices and management"
            icon={RouterIcon}
            color="info"
            status={unifiImporting ? 'Syncing...' : 'In Development'}
            stats={null} // Add stats here when available from API
            onSync={() => setUnifiConfirmOpen(true)}
            syncing={unifiImporting}
            syncResult={unifiResult}
            expanded={unifiExpanded}
            onToggleExpanded={() => setUnifiExpanded(!unifiExpanded)}
            inDevelopment={false}
          >
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              What gets synced:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Switches, APs, and Gateways"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Device status and firmware info"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Network topology and connections"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Available Actions:
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">• Restart devices remotely</Typography>
              <Typography variant="caption" color="text.secondary">• Force provision</Typography>
              <Typography variant="caption" color="text.secondary">• Upgrade firmware</Typography>
              <Typography variant="caption" color="text.secondary">• View in UniFi Controller</Typography>
            </Stack>
          </IntegrationCard>
        </Grid>

        {/* Clearvox Integration Card */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Clearvox VoIP"
            description="Phone system integration for resident communication"
            icon={PhoneIcon}
            color="success"
            status={clearvoxImporting ? 'Syncing...' : 'Connected'}
            stats={clearvoxStats ? [
              { label: 'Users', value: clearvoxStats.totalUsers || 0, color: 'success.main' },
              { label: 'Devices', value: clearvoxStats.totalDevices || 0, color: 'info.main' },
              { label: 'Sites', value: clearvoxStats.totalSites || 0, color: 'primary.main' }
            ] : null}
            onSync={() => {
              setClearvoxConfirmOpen(true);
              setClearvoxSyncProgress({});
              fetchClearvoxSites();
            }}
            syncing={clearvoxImporting}
            syncResult={clearvoxResult}
            expanded={clearvoxExpanded}
            onToggleExpanded={() => setClearvoxExpanded(!clearvoxExpanded)}
          >
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              What gets synced:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Users and phone numbers per site"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Phone device status and information"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Call history (incoming, outgoing, missed)"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </IntegrationCard>
        </Grid>

        {/* 2N Access Commander Integration Card */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="2N Access Commander"
            description="Intercom and access control device integration"
            icon={SecurityIcon}
            color="warning"
            status={twonImporting ? 'Syncing...' : twonCommanders.length > 0 ? `${twonCommanders.length} Commander${twonCommanders.length !== 1 ? 's' : ''} Configured` : 'Not Configured'}
            stats={twonStats ? [
              { label: 'Devices', value: twonStats.totalDevices || 0, color: 'warning.main' },
              { label: 'Online', value: twonStats.onlineDevices || 0, color: 'success.main' },
              { label: 'Offline', value: twonStats.offlineDevices || 0, color: 'error.main' }
            ] : null}
            onSync={twonConfig ? handleTwonSync : () => setTwonConfigOpen(true)}
            syncing={twonImporting}
            syncResult={twonResult}
            expanded={twonExpanded}
            onToggleExpanded={() => setTwonExpanded(!twonExpanded)}
          >
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              What gets synced:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Indoor panels and outdoor stations"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Device status and network information"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Device capabilities (door, camera, RFID, etc.)"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'warning.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Access zone configurations"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Available Actions:
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">• Remote door unlock</Typography>
              <Typography variant="caption" color="text.secondary">• View live camera feeds</Typography>
              <Typography variant="caption" color="text.secondary">• Make test calls (Clearvox integration)</Typography>
              <Typography variant="caption" color="text.secondary">• Device restart</Typography>
              <Typography variant="caption" color="text.secondary">• Zone code management</Typography>
            </Stack>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant={twonCommanders.length === 0 ? "contained" : "outlined"}
                onClick={() => window.location.href = '/admin/integrations/twon/commanders'}
              >
                Manage Commanders {twonCommanders.length > 0 && `(${twonCommanders.length})`}
              </Button>
              {twonCommanders.length > 0 && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setTwonImportOpen(true)}
                  >
                    Import Devices
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setTwonConfigOpen(true)}
                  >
                    Add Commander
                  </Button>
                </>
              )}
            </Box>

            {twonCommanders.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  No 2N Commanders configured yet. Click "Manage Commanders" to add your first Commander.
                </Typography>
              </Alert>
            )}
          </IntegrationCard>
        </Grid>

        {/* Additional Integrations */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Qbus Domotics"
            description="Smart home automation and lighting control"
            icon={LightbulbIcon}
            color="warning"
            comingSoon
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="CARO Files"
            description="Centralized file storage and document management"
            icon={FolderIcon}
            color="secondary"
            comingSoon
          />
        </Grid>

        {/* Emergency Services Integration Card */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Emergency Services"
            description="Alarm routing to emergency service providers (IQmessenger, WGK, etc.)"
            icon={NotificationsActiveIcon}
            color="error"
            status={emergencyStats ? `${emergencyStats.activeIntegrations || 0} Active Integration${emergencyStats.activeIntegrations !== 1 ? 's' : ''}` : 'Loading...'}
            stats={emergencyStats ? [
              { label: 'Providers', value: emergencyStats.totalProviders || 0, color: 'primary.main' },
              { label: 'Sites', value: emergencyStats.totalIntegrations || 0, color: 'info.main' },
              { label: 'Active', value: emergencyStats.activeIntegrations || 0, color: 'success.main' }
            ] : null}
            onSync={() => window.location.href = '/admin/integrations/emergency-services'}
            syncing={false}
            syncResult={null}
            expanded={emergencyExpanded}
            onToggleExpanded={() => setEmergencyExpanded(!emergencyExpanded)}
          >
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Available Providers:
            </Typography>
            <List dense>
              {emergencyProviders.slice(0, 4).map((provider) => (
                <ListItem key={provider.id} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircle sx={{ fontSize: 18, color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={provider.friendlyName}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Features:
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">• Per-site provider configuration</Typography>
              <Typography variant="caption" color="text.secondary">• Alarm code mapping</Typography>
              <Typography variant="caption" color="text.secondary">• Automatic 25-min resend for unresolved alarms</Typography>
              <Typography variant="caption" color="text.secondary">• Email status update links for responders</Typography>
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => window.location.href = '/admin/integrations/emergency-services'}
              >
                Manage Integrations
              </Button>
            </Box>

            {emergencyStats?.byProvider && emergencyStats.byProvider.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Sites by provider: {emergencyStats.byProvider.map(p => `${p.provider} (${p.count})`).join(', ')}
                </Typography>
              </Box>
            )}
          </IntegrationCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Microsoft Dynamics 365 Business Central"
            description="CRM and financial management integration"
            icon={AccountBalanceIcon}
            color="primary"
            comingSoon
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Korfine"
            description="Plaatsbeschrijvingen en waarborgen management"
            icon={DescriptionIcon}
            color="secondary"
            comingSoon
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Personnel Planning"
            description="Staff scheduling and resource management"
            icon={PeopleIcon}
            color="info"
            comingSoon
          />
        </Grid>

        {/* Future Integrations */}
        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Google Workspace"
            description="User and device management from Google"
            icon={CloudSyncIcon}
            color="info"
            comingSoon
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <IntegrationCard
            title="Microsoft 365"
            description="Azure AD and Intune device sync"
            icon={CloudSyncIcon}
            color="info"
            comingSoon
          />
        </Grid>
      </Grid>

      {/* Hexnode Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SyncIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('MDMSettings.confirm_dialog_title')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('MDMSettings.confirm_dialog_description')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Background Import</AlertTitle>
            <Typography variant="body2">
              This import will run in the background and may take several minutes for large device lists (2000+ devices).
              You can monitor progress on this page or continue working elsewhere.
            </Typography>
          </Alert>
          <Alert severity="warning">
            <AlertTitle>{t('MDMSettings.confirm_dialog_warning_title')}</AlertTitle>
            <Typography variant="body2">
              {t('MDMSettings.confirm_dialog_warning_1')}
              <br />
              {t('MDMSettings.confirm_dialog_warning_2')}
              <br />
              {t('MDMSettings.confirm_dialog_warning_3')}
            </Typography>
          </Alert>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
            {t('MDMSettings.confirm_dialog_question')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} size="large">
            {t('MDMSettings.confirm_dialog_cancel')}
          </Button>
          <Button onClick={handleSyncAll} variant="contained" size="large" autoFocus>
            {t('MDMSettings.confirm_dialog_confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* UniFi Confirmation Dialog */}
      <Dialog open={unifiConfirmOpen} onClose={() => setUnifiConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WifiIcon sx={{ mr: 1, color: 'info.main' }} />
            Confirm UniFi Import
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will import all UniFi network devices from your UniFi Controller into CARO.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>What will happen:</AlertTitle>
            <List dense>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Fetch all devices from UniFi Controller"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Link to existing CARO devices by MAC address"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Create new devices for unmatched UniFi equipment"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Alert>
          <Alert severity="warning">
            <AlertTitle>Important Notes</AlertTitle>
            <Typography variant="body2">
              • Ensure UniFi Controller credentials are configured in API settings
              <br />
              • This will create new device entries for UniFi devices not yet in CARO
              <br />
              • Existing device data will be preserved and only UniFi-specific data will be updated
            </Typography>
          </Alert>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
            Do you want to proceed with the import?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUnifiConfirmOpen(false)} size="large">
            Cancel
          </Button>
          <Button onClick={handleUniFiImport} variant="contained" color="info" size="large" autoFocus>
            Import UniFi Devices
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2N Configuration Dialog */}
      <TwoNConfigDialog
        open={twonConfigOpen}
        onClose={() => setTwonConfigOpen(false)}
        onSaved={(config) => {
          fetchTwonStats(); // Refresh commanders list and stats
          setTwonConfigOpen(false);
        }}
        commanderConfig={null}
      />

      {/* 2N Device Import Dialog */}
      <DeviceImportDialog
        open={twonImportOpen}
        onClose={() => setTwonImportOpen(false)}
        onImportComplete={(result) => {
          fetchTwonStats();
        }}
      />

      {/* Clearvox Confirmation Dialog */}
      <Dialog open={clearvoxConfirmOpen} onClose={() => !clearvoxImporting && setClearvoxConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 1, color: 'success.main' }} />
              Clearvox Sync
            </Box>
            {clearvoxSitesLoading && <CircularProgress size={24} />}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select which sites to sync. You can sync all sites at once or choose specific sites.
          </Typography>

          {/* Site Selection */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Available Sites ({clearvoxSites.filter(s => s.connectionStatus === 'connected').length} connected)
              </Typography>
              <Box>
                <Button size="small" onClick={handleClearvoxSelectAll} sx={{ mr: 1 }}>
                  Select All
                </Button>
                <Button size="small" onClick={handleClearvoxDeselectAll}>
                  Deselect All
                </Button>
              </Box>
            </Box>

            {clearvoxSitesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : clearvoxSites.length === 0 ? (
              <Alert severity="info">
                No Clearvox sites found. Make sure locations have Clearvox_APIkey configured.
              </Alert>
            ) : (
              <FormGroup>
                {clearvoxSites.map((site) => {
                  const isConnected = site.connectionStatus === 'connected';
                  const progress = clearvoxSyncProgress[site.siteLocationId];

                  return (
                    <Box
                      key={site.siteLocationId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        mb: 0.5,
                        bgcolor: progress?.status === 'syncing' ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={clearvoxSelectedSites.includes(site.siteLocationId)}
                            onChange={() => handleClearvoxSiteToggle(site.siteLocationId)}
                            disabled={!isConnected || clearvoxImporting}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">
                              {site.siteName}
                            </Typography>
                            {isConnected ? (
                              <Chip
                                size="small"
                                label="Connected"
                                color="success"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            ) : (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Connection Failed
                                    </Typography>
                                    <Typography variant="caption">
                                      {site.error || `Status: ${site.connectionStatus}`}
                                    </Typography>
                                    {site.statusCode && (
                                      <Typography variant="caption" display="block">
                                        HTTP Status: {site.statusCode}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  size="small"
                                  label={site.connectionStatus === 'no_api_key' ? 'No API Key' : 'Error'}
                                  color="error"
                                  icon={<ErrorIcon sx={{ fontSize: 14 }} />}
                                  sx={{ height: 20, fontSize: '0.7rem', cursor: 'help' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        sx={{ flexGrow: 1, m: 0 }}
                      />

                      {/* Sync status/action for this site */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {progress?.status === 'syncing' && (
                          <CircularProgress size={16} />
                        )}
                        {progress?.status === 'success' && (
                          <Tooltip title={`Users: +${progress.stats?.users_added || 0}, Devices: +${progress.stats?.devices_added || 0}`}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          </Tooltip>
                        )}
                        {progress?.status === 'error' && (
                          <Tooltip title={progress.message}>
                            <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                          </Tooltip>
                        )}
                        {!clearvoxImporting && isConnected && !progress && (
                          <Tooltip title="Sync this site only">
                            <IconButton
                              size="small"
                              onClick={() => handleSyncSingleSite(site.siteLocationId)}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </FormGroup>
            )}
          </Paper>

          {/* Progress Summary */}
          {clearvoxImporting && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Syncing in progress...</AlertTitle>
              <Typography variant="body2">
                {Object.values(clearvoxSyncProgress).filter(p => p.status === 'success').length} / {clearvoxSelectedSites.length} sites completed
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(Object.values(clearvoxSyncProgress).filter(p => p.status !== 'syncing').length / clearvoxSelectedSites.length) * 100}
                sx={{ mt: 1 }}
              />
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>What will happen:</AlertTitle>
            <List dense>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Import users and phone numbers from selected sites"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Link phone devices to users"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Update existing users and devices with latest data"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Alert>

          {clearvoxSelectedSites.length > 0 && !clearvoxImporting && (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Ready to sync {clearvoxSelectedSites.length} site{clearvoxSelectedSites.length !== 1 ? 's' : ''}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setClearvoxConfirmOpen(false)}
            size="large"
            disabled={clearvoxImporting}
          >
            {clearvoxImporting ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={handleClearvoxImport}
            variant="contained"
            color="success"
            size="large"
            disabled={clearvoxImporting || clearvoxSelectedSites.length === 0}
            startIcon={clearvoxImporting ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          >
            {clearvoxImporting ? 'Syncing...' : `Sync ${clearvoxSelectedSites.length} Site${clearvoxSelectedSites.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default IntegrationManagement;
